/** Configuration → Change history subtab.
 *
 * Paginated `Tour_Operator_Config_Change` rows for this TO, newest first.
 * Row click opens the MergeConfigDialog so ops can cherry-pick keys from
 * a historic snapshot back into the current in-memory config (the parent
 * applies the merge result to its edit state — no auto-save).
 */
import { Alert, Box, Chip, Tooltip, Typography } from '@mui/material';
import {
  DataGridPro,
  type GridColDef,
  type GridPaginationModel,
} from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import * as topsApi from '@/api/tourOperators';
import type { ConfigChangeListItem } from '@/api/tourOperators';
import { ChangeSetChip } from '@/components/History/ChangeSetChip';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { formatRelativeTime } from '@/utils/format';

import {
  changeActorActions,
  changeSourceActions,
  correlationIdActions,
  ipActions,
  type TenantCtx,
} from '../tenantLinks';

import { MergeConfigDialog } from './MergeConfigDialog';

interface Props {
  tourOperatorId: number;
  /** The operator's code — the `/changes` and correlation searches scope by
   *  tenant id, but the code is what the header and copy actions show. */
  tourOperatorCode: string;
  /** The in-memory config from the parent's editor (may include unsaved
   *  edits). The merge dialog reads from here and emits a new value via
   *  `onMerge`. */
  currentConfig: Record<string, unknown>;
  onMerge: (mergedConfig: Record<string, unknown>) => void;
  /** Controlled selection (URL-driven): the change whose details dialog is open. */
  selectedChangeId: number | null;
  onSelectChange: (changeId: number | null) => void;
}

export function ChangeHistoryTab({
  tourOperatorId,
  tourOperatorCode,
  currentConfig,
  onMerge,
  selectedChangeId,
  onSelectChange,
}: Props) {
  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const ctx = useMemo<TenantCtx>(
    () => ({ id: tourOperatorId, code: tourOperatorCode }),
    [tourOperatorId, tourOperatorCode],
  );

  const query = useQuery({
    queryKey: [
      'tourOperators',
      tourOperatorId,
      'config_changes',
      pagination.page,
      pagination.pageSize,
    ],
    queryFn: () =>
      topsApi.listConfigChanges(tourOperatorId, {
        page: pagination.page + 1,        // backend is 1-indexed
        pageSize: pagination.pageSize,
      }),
    placeholderData: (prev) => prev,
  });

  // The newest row sits at index 0 of page 1 (server orders by `-created_ts`).
  // We only mark "current" on page 1 — on later pages the latest change may
  // not be visible, and marking the page-local top row would be misleading.
  const currentChangeId =
    pagination.page === 0 ? (query.data?.results?.[0]?.id ?? null) : null;

  const columns = useMemo<GridColDef<ConfigChangeListItem>[]>(
    () => [
      {
        field: 'created_ts',
        headerName: 'When',
        width: 220,
        sortable: false,
        renderCell: (p) =>
          p.row.created_ts ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Tooltip title={p.row.created_ts} placement="top" arrow>
                <Box component="span">{formatRelativeTime(p.row.created_ts)}</Box>
              </Tooltip>
              {p.row.id === currentChangeId && (
                <Tooltip
                  title="This is the most recent change — its config_after matches the live Tour_Operator.config."
                  placement="top"
                  arrow
                >
                  <Chip
                    size="small"
                    color="success"
                    label="Current"
                    sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
                  />
                </Tooltip>
              )}
            </Box>
          ) : '—',
      },
      {
        field: 'actor',
        headerName: 'Actor',
        flex: 1,
        minWidth: 180,
        sortable: false,
        renderCell: (p) => {
          if (!p.row.actor) {
            return (
              <Chip size="small" label="system" variant="outlined" />
            );
          }
          const { first_name, last_name, email } = p.row.actor;
          const name = `${first_name} ${last_name}`.trim();
          return (
            // The row click opens the merge dialog; the trigger stops the event
            // so the popover never doubles as "compare this snapshot".
            <ValueActions actions={changeActorActions(p.row.actor)}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {name || email || `User #${p.row.actor.id}`}
                </Typography>
                {name && email && (
                  <Typography variant="caption" color="text.secondary">
                    {email}
                  </Typography>
                )}
              </Box>
            </ValueActions>
          );
        },
      },
      {
        field: 'source',
        headerName: 'Source',
        width: 240,
        sortable: false,
        renderCell: (p) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <ValueActions chip actions={changeSourceActions(p.row.source, ctx)}>
              <Chip
                size="small"
                label={p.row.source}
                variant="outlined"
                sx={{ fontSize: 11 }}
              />
            </ValueActions>
            {/* An AI change's row: the chip opens the change set (new tab). */}
            <ChangeSetChip changeSet={p.row.change_set} sx={{ minWidth: 0 }} />
          </Box>
        ),
      },
      {
        field: 'comment',
        headerName: 'Comment',
        flex: 2,
        minWidth: 220,
        sortable: false,
        renderCell: (p) => (
          <Tooltip title={p.row.comment || ''} placement="top" arrow disableHoverListener={!p.row.comment}>
            <Typography variant="body2" sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: p.row.comment ? 'text.primary' : 'text.disabled',
              fontStyle: p.row.comment ? 'normal' : 'italic',
            }}>
              {p.row.comment || '(no description)'}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'ip',
        headerName: 'IP',
        width: 130,
        sortable: false,
        renderCell: (p) =>
          p.row.ip ? (
            <ValueActions actions={ipActions(p.row.ip)}>
              <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                {p.row.ip}
              </Box>
            </ValueActions>
          ) : <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>,
      },
      {
        field: 'correlation_id',
        headerName: 'Correlation ID',
        width: 260,
        sortable: false,
        renderCell: (p) =>
          p.row.correlation_id ? (
            <ValueActions actions={correlationIdActions(p.row.correlation_id, ctx)}>
              <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                {p.row.correlation_id}
              </Box>
            </ValueActions>
          ) : <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>,
      },
      {
        field: 'changed_paths_count',
        headerName: 'Keys',
        width: 80,
        align: 'right',
        headerAlign: 'right',
        sortable: false,
        renderCell: (p) => (
          <Chip
            size="small"
            label={p.row.changed_paths_count}
            color={p.row.changed_paths_count > 0 ? 'primary' : 'default'}
            variant="outlined"
            sx={{ height: 20, minWidth: 40 }}
          />
        ),
      },
    ],
    [currentChangeId, ctx],
  );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Every change to <code>Tour_Operator.config</code> is recorded here, newest first.
        Click a row to compare its snapshot with the current configuration and merge selected keys.
      </Typography>

      {query.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Couldn’t load change history: {(query.error as Error).message}
        </Alert>
      )}

      <DataGridPro<ConfigChangeListItem>
        rows={query.data?.results ?? []}
        rowCount={query.data?.count ?? 0}
        getRowId={(r) => r.id}
        columns={columns}
        initialState={{ columns: { columnVisibilityModel: { correlation_id: false } } }}
        loading={query.isLoading || query.isFetching}
        autoHeight
        paginationMode="server"
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        pageSizeOptions={[10, 25, 50, 100]}
        disableColumnFilter
        disableRowSelectionOnClick
        onRowClick={(p) => onSelectChange(p.row.id)}
        sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        slots={{
          noRowsOverlay: () => (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              No configuration changes have been recorded for this tour operator yet.
            </Box>
          ),
        }}
      />

      <MergeConfigDialog
        open={selectedChangeId !== null}
        tourOperatorId={tourOperatorId}
        changeId={selectedChangeId}
        currentConfig={currentConfig}
        onMerge={(merged) => {
          onMerge(merged);
          onSelectChange(null);
        }}
        onCancel={() => onSelectChange(null)}
      />
    </Box>
  );
}
